// src/components/auth/Register.jsx - Beta Registration Component - Rebuilt with shadcn
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Rocket, Building2, User, Mail, ArrowRight, CheckCircle } from 'lucide-react';

// Import shadcn components
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Field, FieldLabel, FieldDescription } from '../ui/field';
import { Input, PasswordInput } from '../ui';
import { Alert, AlertDescription } from '../ui/alert';

// Import the real auth context
import { useAuth } from '../../contexts/AuthContext.jsx';

const Register = () => {
  const [step, setStep] = useState(1); // 1: Beta Code, 2: Registration Form
  const [betaCode, setBetaCode] = useState('');
  const [formData, setFormData] = useState({
    business_name: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: ''
  });

  const navigate = useNavigate();
  const { verifyBetaCode, registerWithBetaCode, loading, error, clearError } = useAuth();

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      // Verify the beta code
      await verifyBetaCode(betaCode);
      // If successful, move to registration form
      setStep(2);
    } catch (err) {
      // Error is handled by AuthContext
      console.error('Beta code verification failed:', err);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    clearError();

    // Validate passwords match
    if (formData.admin_password !== formData.confirm_password) {
      // We can't directly set error in AuthContext, so we'll show an alert
      alert('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.admin_password.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    try {
      const success = await registerWithBetaCode(
        betaCode,
        formData.business_name,
        formData.admin_name,
        formData.admin_email,
        formData.admin_password
      );

      if (success) {
        console.log('✅ Registration successful, redirecting to onboarding');
        navigate('/onboarding');
      }
    } catch (err) {
      // Error is handled by AuthContext
      console.error('Registration failed:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        {/* Step 1: Beta Code Entry */}
        {step === 1 && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-white">Join the Beta</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your beta access code to get started
              </CardDescription>
            </CardHeader>

            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <Field>
                  <FieldLabel htmlFor="betaCode">Beta Access Code *</FieldLabel>
                  <Input
                    type="text"
                    id="betaCode"
                    value={betaCode}
                    onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                    placeholder="XXX-XXXXX"
                    required
                    disabled={loading}
                    maxLength={9}
                    className="uppercase tracking-wider"
                  />
                  <FieldDescription>
                    Enter the 9-character code you received
                  </FieldDescription>
                </Field>

                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={!betaCode || betaCode.length < 9 || loading}
                >
                  {loading ? 'Verifying...' : (
                    <>
                      Verify Code
                      <CheckCircle className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-slate-900/40 px-2 text-slate-400">Already have an account?</span>
                </div>
              </div>

              <Button
                asChild
                variant="outline"
                className="w-full"
                disabled={loading}
              >
                <Link to="/login">Sign In</Link>
              </Button>
            </CardContent>
          </>
        )}

        {/* Step 2: Registration Form */}
        {step === 2 && (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-white">Create Your Account</CardTitle>
              <CardDescription className="text-slate-400">
                Set up your business and admin account
              </CardDescription>
            </CardHeader>

            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleRegistrationSubmit} className="space-y-6">
                {/* Business Name */}
                <Field>
                  <FieldLabel htmlFor="businessName">Business Name *</FieldLabel>
                  <Input
                    type="text"
                    id="businessName"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="ABC Pool Service"
                    leftIcon={Building2}
                    required
                    disabled={loading}
                  />
                </Field>

                {/* Admin Name */}
                <Field>
                  <FieldLabel htmlFor="adminName">Your Name *</FieldLabel>
                  <Input
                    type="text"
                    id="adminName"
                    value={formData.admin_name}
                    onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                    placeholder="John Doe"
                    leftIcon={User}
                    required
                    disabled={loading}
                  />
                </Field>

                {/* Email */}
                <Field>
                  <FieldLabel htmlFor="adminEmail">Email Address *</FieldLabel>
                  <Input
                    type="email"
                    id="adminEmail"
                    value={formData.admin_email}
                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                    placeholder="john@abcpool.com"
                    leftIcon={Mail}
                    required
                    disabled={loading}
                  />
                </Field>

                {/* Password */}
                <Field>
                  <FieldLabel htmlFor="adminPassword">Password *</FieldLabel>
                  <PasswordInput
                    id="adminPassword"
                    value={formData.admin_password}
                    onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                    placeholder="At least 8 characters"
                    required
                    disabled={loading}
                  />
                </Field>

                {/* Confirm Password */}
                <Field>
                  <FieldLabel htmlFor="confirmPassword">Confirm Password *</FieldLabel>
                  <PasswordInput
                    id="confirmPassword"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Re-enter your password"
                    required
                    disabled={loading}
                  />
                </Field>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={loading}
                    className="flex-1"
                  >
                    Back
                  </Button>

                  <Button
                    type="submit"
                    variant="default"
                    disabled={loading || !formData.business_name || !formData.admin_name || !formData.admin_email || !formData.admin_password || !formData.confirm_password}
                    className="flex-1"
                  >
                    {loading ? 'Creating Account...' : (
                      <>
                        Create Account
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default Register;
